import { Box, Typography, useTheme } from '@mui/material'
import { GridColDef, GridColType } from '@mui/x-data-grid-premium'
import { AddressPill } from '@react/components/addresses/AddressPill'
import ViewNumber from '@react/components/elements/ViewNumber'
import { ReportDescriptor } from '@react/reporting/ReportDescriptors'
import { otAssert } from '@shared/conversions'
import { logOT } from '@shared/loggingShared'
import { ReportingColumnVisibilityEnum, StringTypeEnum } from '@shared/SchemaTypes'

import dayjs from 'dayjs'
import React, { ReactNode } from 'react'
import * as yup from 'yup'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(timezone)

function ExplorerHeader(props: { label: string }): React.ReactElement {
  const { label } = props
  const theme = useTheme()
  return (
    <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
      <Typography
        sx={{
          wordBreak: 'break-word',
          display: 'inline-block',
          maxWidth: '100%',
        }}
        bgcolor={theme.palette.background.default}
        color={theme.palette.info.main}
        align={'left'}
        variant={'captionBold'}
      >
        {label}
      </Typography>
    </Box>
  )
}

const valueFromRow = (row: any, property: string): any => {
  const propArray = property.split('.')
  let value = row
  for (let i = 0; i < propArray.length; i++) {
    const prop = propArray[i]
    otAssert(prop, `prop is undefined ${property} `)
    otAssert(value, `prop is undefined ${property} `)

    value = value[prop]

    if (value === undefined) {
      return undefined
    }
  }
  return value
}
const columnFromSchema = (fieldSchemaDescription: yup.SchemaDescription, property: string): GridColDef | undefined => {
  const label: string = fieldSchemaDescription.label ?? ' '
  const stringType = fieldSchemaDescription.meta!.stringType

  if (fieldSchemaDescription.meta?.columnVisibility == ReportingColumnVisibilityEnum.Disabled) {
    return undefined
  }
  const fieldType = fieldSchemaDescription.type
  let columnType = fieldType
  if (
    fieldType === 'string' &&
    (stringType === StringTypeEnum.STABLE_COINS || stringType === StringTypeEnum.PPT_SHARES)
  ) {
    columnType = 'erc20'
  }
  if (stringType === StringTypeEnum.ETH_DATE) {
    columnType = 'string'
  }
  if (stringType === StringTypeEnum.ETH_DATE_TIME) {
    columnType = 'string'
  }
  let sortComparator: undefined | ((a: any, b: any) => number) = undefined
  if (stringType === StringTypeEnum.ETH_DATE_TIME_STRING) {
    columnType = 'string'
    sortComparator = (a, b) => {
      const aSplit = a.split(/[-\s]/)
      const bSplit = b.split(/[-\s]/)
      const aFlip: string = aSplit[2] + aSplit[1] + aSplit[0]
      const bFlip: string = bSplit[2] + bSplit[1] + bSplit[0]

      return Number(aFlip) - Number(bFlip)
    }
  }
  return {
    field: property,
    renderHeader: (_): ReactNode => {
      return <ExplorerHeader label={label} />
    },
    headerName: label,
    type: columnType as GridColType,
    width: fieldSchemaDescription.meta?.columnWidth ?? 150,
    sortComparator: sortComparator,
    renderCell: (params) => {
      let value = valueFromRow(params.row, property)
      if (params.aggregation) {
        value = params.value
      }

      const poolTokenSymbol =
        params.row.symbol ?? params.row.poolOverviewCTO?.symbol ?? params.row.poolAccountCTO?.symbol ?? ''
      const liquidityTokenSymbol =
        params.row.liquidityTokenSymbol ??
        params.row.poolOverviewCTO?.liquidityTokenSymbol ??
        params.row.poolAccountCTO?.liquidityTokenSymbol ??
        ''

      if (stringType === StringTypeEnum.ETH_ADDR) {
        if (!value) return <Box sx={{ textAlign: 'center', marginRight: 6 }}>-</Box>
        return (
          <AddressPill
            addr={value}
            style={{ height: 20 }}
            labelStyle={{ fontSize: '12px', lineHeight: '1', zIndex: 5 }}
          />
        )
      }

      if (stringType === StringTypeEnum.TX_HASH) {
        return (
          <div className="">
            <AddressPill
              addr={value}
              style={{ height: 20 }}
              labelStyle={{ fontSize: '12px', lineHeight: '12px', zIndex: 5 }}
              hasEtherScanLink={true}
            />
          </div>
        )
      }
      const stateToString: undefined | Record<string, string> = fieldSchemaDescription.meta?.stateToString
      if (value && stateToString) {
        return (
          <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
            <Typography align={'left'} variant={'caption'}>
              {stateToString[value]}
            </Typography>
          </Box>
        )
      }
      if (
        fieldType === 'number' ||
        stringType === StringTypeEnum.EXCHANGE_RATE ||
        stringType === StringTypeEnum.STABLE_COINS ||
        stringType === StringTypeEnum.PPT_SHARES ||
        stringType === StringTypeEnum.APR ||
        stringType === StringTypeEnum.SINGLE_DAY_APR
      ) {
        return (
          <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
            {value !== undefined && (
              <ViewNumber
                value={value}
                label={label}
                inTable={true}
                fieldSchema={fieldSchemaDescription}
                dataValue={value}
                poolTokenSymbol={poolTokenSymbol}
                liquidityTokenSymbol={liquidityTokenSymbol}
              />
            )}
          </Box>
        )
      }

      if (fieldType === 'array') {
        const arrayValue = value as any[]
        return (
          <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
            {arrayValue.map((item: object, index: number) => {
              if (index < 2) {
                return (
                  <Typography key={index} align={'left'} variant={'caption'}>
                    {JSON.stringify(item)}
                  </Typography>
                )
              }
              return <></>
            })}
            {arrayValue.length >= 2 && (
              <Typography align={'left'} variant={'caption'}>
                ...
              </Typography>
            )}
          </Box>
        )
      }
      return (
        <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
          <Typography align={'left'} variant={'caption'}>
            {value?.toString()}
          </Typography>
        </Box>
      )
    },

    valueFormatter: (value: any) => {
      if (value === undefined) {
        return ''
      }

      if (stringType === StringTypeEnum.STABLE_COINS) {
        return `${(value / 1e6).toFixed(2)}`
      }
      if (stringType === StringTypeEnum.PPT_SHARES) {
        return `${(value / 1e6).toFixed(2)}`
      }

      if (stringType === StringTypeEnum.ETH_DATE) {
        otAssert(value instanceof Date)
        const dayJS = dayjs(value)
        return dayJS.utc().format('YYYY-MM-DD UTC')
      }
      if (stringType === StringTypeEnum.ETH_TIME) {
        otAssert(value instanceof Date)
        const dayJS = dayjs(value)
        return dayJS.utc().format('HH:mm:ss UTC')
      }
      if (stringType === StringTypeEnum.ETH_DATE_TIME) {
        otAssert(value instanceof Date)
        const dayJS = dayjs(value)
        return dayJS.utc().format('YYYY-MM-DD HH:mm:ss UTC')
      }
      if (stringType === StringTypeEnum.ETH_TIME_OF_DAY) {
        // seconds from 0:00 UTC e.g. 64800 is 18:00 UTC
        const secondsSinceMidnight = Number(value)
        const hour = Math.floor(secondsSinceMidnight / 3600)
        const rem = secondsSinceMidnight % 3600
        const min = Math.floor(rem / 60)
        const minDisplay = min < 10 ? `0${min}` : `${min}`
        return `${hour}:${minDisplay}  UTC`
      }
      if (stringType === StringTypeEnum.SINGLE_DAY_APR) {
        return `${(Number(value) / 1e8 / 1e8).toFixed(6)} %`
      }
      if (stringType === StringTypeEnum.APR) {
        return `${(Number(value) / 1e2).toFixed(2)} %`
      }
      if (fieldType === 'array') {
        return JSON.stringify(value)
      }

      return `${value}`
    },

    valueGetter: (_value: any, row?: any, _data?: any, _apiRef?: any) => {
      // istanbul ignore next
      if (!row) return ''
      const value = valueFromRow(row, property)
      if (
        stringType === StringTypeEnum.ETH_DATE ||
        stringType === StringTypeEnum.ETH_DATE_TIME ||
        stringType === StringTypeEnum.ETH_TIME
      ) {
        const d = new Date(Number(value) * 1000)
        return d
        //        return `${d.getUTCFullYear()}-${d.getUTCMonth() + 1}-${d.getUTCDate()} UTC`
        //        return (value + 2209161600) / (3600 * 24)
      }

      return value
    },
  }
}

type ColumnInfo = {
  prop: string
  columnOrder: number
  columnDef: GridColDef
}

function buildColumnsRecursive(
  prefix: string,
  fields: Record<string, yup.SchemaDescription>,
  reportDescriptor: ReportDescriptor,
  columnInfos: ColumnInfo[],
  columnVisibilityModel: Record<string, boolean>
) {
  const keys = Object.keys(fields)

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]
    const field = fields[key]
    const prop = prefix + key
    if (field.type === 'object') {
      buildColumnsRecursive(
        prop + '.',
        (field as yup.SchemaObjectDescription).fields as Record<string, yup.SchemaDescription>,
        reportDescriptor,
        columnInfos,
        columnVisibilityModel
      )
    } else {
      const column = columnFromSchema(field, prop)
      // istanbul ignore next
      if (column) {
        const columnVisibility = field.meta?.columnVisibility ?? ReportingColumnVisibilityEnum.DefaultHidden
        columnInfos.push({
          prop,
          columnOrder: field.meta?.columnOrder ?? Number.MAX_SAFE_INTEGER,
          columnDef: column,
        })
        columnVisibilityModel[prop] = columnVisibility === ReportingColumnVisibilityEnum.DefaultVisible
      }
    }
  }
}

export function reportColumnsForDescriptor(reportDescriptor: ReportDescriptor): {
  columns: GridColDef[]
  columnVisibilityModel: Record<string, boolean>
  columnOrder: Record<number, string>
} {
  const columnInfos: ColumnInfo[] = []
  const columnVisibilityModel: Record<string, boolean> = {}
  buildColumnsRecursive(
    '',
    reportDescriptor.schema.describe().fields as Record<string, yup.SchemaDescription>,
    reportDescriptor,
    columnInfos,
    columnVisibilityModel
  )

  const columns: GridColDef[] = columnInfos.sort((a, b) => a.columnOrder - b.columnOrder).map((ci) => ci.columnDef)
  // prettier-ignore
  const columnOrder = columnInfos.reduce((acc, ci) => {
    if (ci.columnOrder && ci.columnOrder !== Number.MAX_SAFE_INTEGER) {
      acc[ci.columnOrder] = ci.prop
    }
    return acc
  }, {} as Record<number, string>)

  logOT.info('columnOrder', columnOrder)

  return { columns, columnVisibilityModel, columnOrder }
}

export const ReportingColumnsTestExports = {
  columnFromSchema,
}
